import { Paper, Typography } from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { App, Events, User } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, LabelList, Tooltip, XAxis } from "recharts";
import { v4 as uuid } from "uuid";

import { useStateValue } from "../../config/AppContext";
import { Action, EventType, MonthTypes } from "../../types/StaticTypes";
import { ConcreteKey } from "../../types/utils";
import { formatDate } from "../../utils/time-utils";
import { SessionListings } from "../Schedules/SessionListings";
import LoadingSpinnerComponent from "../Utilities/LoadingSpinner";
import { IdRoute } from "./ClientEdit";

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    BarChartCss: {
      backgroundColor: "#5A9AE5",
      "& .recharts-xAxis": {
        fontSize: 13,
      },
      "& text": {
        fill: "rgba(255,255,255,0.66)",
      },
    },
    root: {
      flexGrow: 1,
    },
    sessionsDiv: {
      padding: "0 16px",
    },
    heading: {
      fontSize: 14,
      fontStyle: "normal",
      color: "#9b9b9b",
      marginTop: 15,
      marginBottom: 15,
    },

    paper: {
      height: 107,
      padding: "8px 12px",
      marginBottom: 15,
      cursor: "pointer",
      "& .MuiGrid-spacing-xs-3 > .MuiGrid-item": {
        width: "100%",
      },
    },
  })
);

export const ClientSessionsRoute = (props: IdRoute) => (
  <ClientSessions id={props.match.params.id} currentYear={new Date().getFullYear()} />
);

type Event = Events.ResponseServerEvent<Events.SessionFullThatUsedToBePreview>;
export type SessionEvent = ConcreteKey<Event, "session">;

function isSessionEvent(ev: Event) {
  return ev.type === EventType.Session && typeof ev.session === "object";
}

export default function ClientSessions(props: { id: string; currentYear: number }) {
  const [loading, setLoading] = React.useState(false);
  const { t } = useTranslation();
  const { clients, Methods, currentYear } = useStateValue() as {
    clients: App.State["clients"];
    Methods: App.Methods;
    currentYear: number;
  };

  const classes = useStyles();
  const clientSessionReducer: React.Reducer<
    {
      currentClient?: User.IUserClient;
      currentMonth: number;
      currentYear: number;
      clientSessions?: SessionEvent[];
    },
    Action
  > = (state, { type, payload }) => {
    const refreshSessions = async () => {
      if (!state.currentClient) return;
      const params = {
        customer_id: state.currentClient.id,
        from: formatDate(new Date(state.currentYear, 0, 1), 3),
        to: formatDate(new Date(state.currentYear, 11, 31), 3),
        types: [EventType.Session],
      };
      state.clientSessions = (await Methods.getEvents(params)) as SessionEvent[];
      console.assert(
        state.clientSessions.map(isSessionEvent),
        "Client event sessions contained something else than just sessions?!"
      );

      if (payload.callback)
        return dispatch({
          type: "refresh",
          payload: { state, callback: payload.callback },
        });
      return dispatch({ type: "refresh", payload: state });
    };
    switch (type) {
      case "init":
        state.clientSessions = payload;
        setLoading(false);
        break;
      case "refresh":
        if (payload.state) state = payload.state;
        else state = payload;
        if (loading) setLoading(false);
        if (payload.callback) payload.callback();
        break;
      case "schedule":
        if (!loading) setLoading(true);
        refreshSessions();
        break;
      case "set-month":
        if (!loading) setLoading(true);
        setTimeout(() => {
          state.currentMonth = payload;
          setLoading(false);
        }, 0);
        break;
      case "set-year":
        state.currentYear = payload;
        if (!loading) setLoading(true);
        refreshSessions();
        break;
      case "update-workout":
        if (payload && payload.params) {
          if (!loading) setLoading(true);
          Methods.updateScheduledWorkout(payload.params).then(refreshSessions);
          break;
        }
        if (payload) {
          if (!loading) setLoading(true);
          Methods.updateScheduledWorkout(payload).then(refreshSessions);
        }
        break;
      case "update-session":
        if (payload && payload.params) {
          if (!loading) setLoading(true);
          const newPayload = {
            ...payload.params,
            start: `${payload.params.start}`.substr(0, 17),
            end: `${payload.params.end}`.substr(0, 17),
            created: formatDate(new Date(), 6),
          };
          Methods.putEvents(newPayload, newPayload.id).then(refreshSessions);
          break;
        }
        if (payload) {
          if (!loading) setLoading(true);
          const newPayload = {
            ...payload,
            start: `${payload.start}`.substr(0, 17),
            end: `${payload.end}`.substr(0, 17),
            created: formatDate(new Date(), 6),
          };
          Methods.putEvents(newPayload, newPayload.id).then(refreshSessions);
        }
        break;
      case "complete-session":
        if (payload && payload.event) {
          if (!loading) setLoading(true);

          const newPayload = {
            ...payload.event,
            session: {
              id: payload.event.session.id,
              customer_id: payload.event.session.customer.map((el: any) => el.id),
              workout_id: payload.event.session.workout ? payload.event.session.workout.id : null,
              notes: payload.event.session.notes,
              state: "COMPLETED",
              state_reason: payload.event.session.state_reason,
              state_notes: payload.event.session.state_notes,
              state_time: payload.event.session.state_time
                ? payload.event.session.state_time
                : formatDate(new Date(), 10),
              is_booked: payload.event.session.is_booked,
            },
            created: formatDate(new Date(), 6),
          };
          Methods.putEvents(newPayload, newPayload.id).then(refreshSessions);
        }
        break;
      case "share-session":
        if (payload && payload.params && payload.event && !loading) {
          setLoading(true);
          Methods.shareSession(payload.params).then(refreshSessions);
        }
        break;
      case "cancel-session":
        if (payload && payload.event && !loading) {
          setLoading(true);
          const newPayload = {
            ...payload.event,
            session: {
              id: payload.event.session.id,
              customer_id: payload.event.session.customer.map((el: any) => el.id),
              workout_id: payload.event.session.workout ? payload.event.session.workout.id : null,
              notes: payload.event.session.notes,
              state: "CANCELLED",
              state_reason: payload.event.session.state_reason,
              state_notes: payload.event.session.state_notes,
              state_time: formatDate(new Date(), 10),
              is_booked: payload.event.session.is_booked,
            },
            created: formatDate(new Date(), 6),
          };
          Methods.putEvents(newPayload, newPayload.id).then(refreshSessions);
        }
        break;
      case "addnew":
        if (payload && !loading) {
          setLoading(true);
          payload = {
            id: null,
            start: payload.from,
            end: payload.to,
            type: payload.type,
            name: payload.name,
            timezone: payload.timezone,
            session:
              payload.type === "SESSION"
                ? {
                    id: uuid(),
                    customer_id: payload.customer_id,
                    state: "NONE",
                    is_booked: false,
                  }
                : null,
            created: formatDate(new Date(), 6),
          };
          Methods.postEvents(payload).then(refreshSessions);
        }
        break;
      case "remove-event":
        if (payload && !loading) {
          setLoading(true);
          Methods.delEvents(payload.id).then(refreshSessions);
        }
        break;
      default:
        throw new Error(t("Invalid action type"));
    }
    return state;
  };

  const clientToBe = props.id;

  const [state, dispatch] = React.useReducer(clientSessionReducer, {
    currentClient: clients && clients.find((client) => client.id === clientToBe),
    currentMonth: new Date().getMonth(),
    currentYear: currentYear || props.currentYear,
  });

  React.useEffect(() => {
    if (state.currentClient && !loading && !state.clientSessions) {
      const params = {
        customer_id: state.currentClient.id,
        from: formatDate(new Date(state.currentYear, 0, 1), 3),
        to: formatDate(new Date(state.currentYear, 11, 31), 3),
        types: [EventType.Session],
      };
      Methods.getEvents(params).then((events) => dispatch({ type: "init", payload: events }));
      return setLoading(true);
    }
  }, [state, loading, Methods]);

  React.useEffect(() => {
    if (window.innerWidth > 700 && state.currentYear !== props.currentYear) {
      dispatch({ type: "set-year", payload: props.currentYear });
    }
  }, [props.currentYear, state.currentYear]);

  return (
    <div id="client-sessions">
      <section>
        <ChartComponent
          clientSessions={state.clientSessions}
          dispatch={dispatch}
          currentYear={state.currentYear}
          currentMonth={state.currentMonth}
        />
      </section>
      <section style={{ marginBottom: 15 }} className={classes.sessionsDiv}>
        <SessionsListingsComponent {...state} dispatch={dispatch} />
      </section>
    </div>
  );
}

type ChildComponentProps = {
  currentMonth: number;
  currentYear: number;
  clientSessions?: SessionEvent[];
  dispatch: React.Dispatch<Action>;
};

const ChartComponent: React.FC<ChildComponentProps> = ({ clientSessions, dispatch }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  if (!clientSessions) return <></>;

  const sortedClientSessions: (index: number) => SessionEvent[] = (index) => {
    return clientSessions.filter((el) => {
      if (!el.start) return false;
      const date = new Date(el.start);
      return date.getMonth() === index;
    });
  };
  const filterCompleted = (sessions: SessionEvent[]) => {
    return sessions.filter((el) => el.session.state === "COMPLETED").length;
  };
  const filterCancelled = (sessions: SessionEvent[]) => {
    return sessions.filter((el) => el.session.state === "CANCELLED").length;
  };
  const filterUpcoming = (sessions: SessionEvent[]) => {
    return sessions.filter((el) => el.session.state === "NONE").length;
  };
  const data = [
    [
      "Month",
      t("Completed"),
      { role: "annotation" },
      t("Cancelled"),
      { role: "annotation" },
      t("Upcoming"),
      { role: "annotation" },
    ],
  ];
  for (let i = 0; i < 12; i++) {
    data[i + 1] = [
      MonthTypes[i],
      //@ts-ignore
      filterCompleted(sortedClientSessions(i)),
      filterCompleted(sortedClientSessions(i)).toString(),
      //@ts-ignore
      filterCancelled(sortedClientSessions(i)),
      filterCancelled(sortedClientSessions(i)).toString(),
      //@ts-ignore
      filterUpcoming(sortedClientSessions(i)),
      filterUpcoming(sortedClientSessions(i)).toString(),
    ];
  }

  let BarChartData = [];
  for (let i = 0; i < 12; i++) {
    BarChartData[i] = {
      month: `${t(`${MonthTypes[i]}`)}`,
      completed: filterCompleted(sortedClientSessions(i)),
      cancelled: filterCancelled(sortedClientSessions(i)),
      upcoming: filterUpcoming(sortedClientSessions(i)),
    };
  }

  const valueAccessor = (attribute: any) => (payload: any) => {
    if (payload[attribute]) {
      return payload[attribute];
    }
  };
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active) {
      return payload[0].value || payload[1].value || payload[2].value ? (
        <div
          //  className="custom-tooltip"
          style={{ background: "white", padding: "10px", fontSize: "14px" }}
        >
          <p style={{ marginBottom: "unset", textTransform: "uppercase" }}>{`${t(`${label}`)}`}</p>
          <p
            style={{
              marginBottom: "unset",
              textTransform: "capitalize",
              color: "rgb(139, 184, 236)",
              padding: "4px 0px",
            }}
          >
            {" "}
            {`${t(`${payload[0].dataKey}`).toLowerCase()} : ${payload[0].value}`}
          </p>
          <p
            style={{
              marginBottom: "unset",
              textTransform: "capitalize",
              color: "rgb(245, 166, 35)",
              padding: "4px 0px",
            }}
          >{`${t(`${payload[1].dataKey}`).toLowerCase()} : ${payload[1].value}`}</p>
          <p
            style={{
              marginBottom: "unset",
              textTransform: "capitalize",
              color: "rgb(237, 77, 77)",
              padding: "4px 0px",
            }}
          >{`${t(`${payload[2].dataKey}`).toLowerCase()} : ${payload[2].value}`}</p>
        </div>
      ) : null;
    }

    return null;
  };
  return (
    <div className={classes.BarChartCss} style={{ padding: "15px 0" }}>
      <BarChart
        style={
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
            ? { width: "100%", overflowX: "auto", overflowY: "hidden" }
            : {}
        }
        width={492}
        className={classes.BarChartCss}
        barSize={30}
        height={465}
        data={BarChartData}
      >
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          allowDataOverflow
          onClick={({ index }) => {
            return dispatch({ type: "set-month", payload: index });
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar
          dataKey="completed"
          stackId="a"
          fill="#8bb8ec"
          label={{
            fill: "#FFFFFF",
            fontSize: 10,
            position: "insideTop",
            formatter: (value) => {
              return value !== 0 ? "" : "";
            },
          }}
          onClick={(e, x) => {
            return dispatch({ type: "set-month", payload: x });
          }}
        >
          <LabelList valueAccessor={valueAccessor("completed")} />
        </Bar>

        <Bar
          dataKey="upcoming"
          stackId="a"
          fill="#f5a623"
          label={{
            fill: "#FFFFFF",
            fontSize: 10,
            position: "insideTop",
            formatter: (value) => {
              return value !== 0 ? "" : "";
            },
          }}
          onClick={(e, x) => {
            return dispatch({ type: "set-month", payload: x });
          }}
        >
          <LabelList valueAccessor={valueAccessor("upcoming")} />
        </Bar>
        <Bar
          dataKey="cancelled"
          stackId="a"
          fill="#ed4d4d"
          label={{
            fill: "#FFFFFF",
            fontSize: 10,
            position: "insideTop",
            formatter: (value) => {
              return value !== 0 ? "" : "";
            },
          }}
          onClick={(e, x) => {
            console.log(e, x);
            return dispatch({ type: "set-month", payload: x });
          }}
        >
          <LabelList valueAccessor={valueAccessor("cancelled")} />
        </Bar>
      </BarChart>
    </div>
  );
};

const SessionsListingsComponent: React.FC<ChildComponentProps> = ({
  currentMonth,
  currentYear,
  clientSessions,
  dispatch,
}) => {
  const classes = useStyles();

  const currentMonthSessions =
    clientSessions &&
    clientSessions.filter((el) => {
      if (!el.start) return false;
      const date = new Date(el.start);
      return (
        date.getMonth() ===
        (currentMonth || currentMonth === 0 ? currentMonth : new Date().getMonth())
      );
    });

  const { t } = useTranslation();

  const generateSessionBlock = (currentEvent: SessionEvent) => {
    return (
      <Paper key={currentEvent.id} className={classes.paper}>
        <SessionListings key={currentEvent.id} event={currentEvent} dispatch={dispatch} />
      </Paper>
    );
  };

  if (!currentMonthSessions)
    return (
      <>
        <span style={{ textAlign: "center", display: "block" }}>{t("Loading Sessions...")} </span>
        <LoadingSpinnerComponent />
      </>
    );

  if (currentMonthSessions.length === 0)
    return (
      <Typography variant="h5" component="h5" className={classes.heading}>
        {t("no sessions for")}{" "}
        {/* {console.log('currentMonth', currentMonth,  new Date().getMonth())} */}
        {t(
          `${MonthTypes[
            currentMonth || currentMonth === 0 ? currentMonth : new Date().getMonth()
          ].toLowerCase()}`
        )}{" "}
        {currentYear}
      </Typography>
    );

  return (
    <>
      <Typography variant="h5" component="h5" className={classes.heading}>
        {t(`${MonthTypes[currentMonth ? currentMonth : new Date().getMonth()].toLowerCase()}`)}{" "}
        {currentYear}
      </Typography>
      {currentMonthSessions.map(generateSessionBlock)}
    </>
  );
};
